import { useState } from 'react';
import { useRouter } from 'next/router';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Text, TextArea } from 'components/common/form';
import {
  ProfileCompetencyMappingChat,
  ProfileCompetencyMappingHeader
} from '.';

import type { RunCompetencyMappingRes } from 'shared-types/responses';

export type ChatMessage = {
  id: string;
  sender: 'assistant' | 'user';
  text: string;
};

type RoleDetails = {
  experienceYears: number;
  focusAreas?: string;
  jobTitle: string;
};

export type CompetencyMappingState = {
  chatMessages: ChatMessage[];
  isAnalyzing: boolean;
  isConversationComplete: boolean;
  isWaitingForReply: boolean;
  phase: 'chat' | 'form';
  roleDetails: RoleDetails | undefined;
};

const ProfileCompetencyMapping = () => {
  const { dispatch } = useDispatch();
  const { messages } = useReduxState();

  const router = useRouter();

  const initialState: CompetencyMappingState = {
    chatMessages: [],
    isAnalyzing: false,
    isConversationComplete: false,
    isWaitingForReply: false,
    phase: 'form',
    roleDetails: undefined
  };

  const [state, setState] = useState<CompetencyMappingState>(initialState);

  const handleHide = () => {
    setState(initialState);
  };

  const handleAnalyzeAndShowResults = async () => {
    try {
      if (
        state.isAnalyzing ||
        state.isWaitingForReply ||
        !state.chatMessages.length
      ) {
        return;
      }

      setState({ ...state, isAnalyzing: true });

      const { data } = await api.post<RunCompetencyMappingRes>(
        '/admin/competency-mapping/analyze',
        {
          messages: state.chatMessages.map(({ sender, text }) => ({
            role: sender === 'assistant' ? 'model' : 'user',
            text
          }))
        }
      );

      dispatch.addNotice({
        text: messages.competencyMappingAnalysisSuccess,
        type: 'success'
      });

      router.push(`/profile/competency-mappings/${data.mappingId}`);

      return true;
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setState({ ...state, isAnalyzing: false });
    }
  };

  const handleSubmit = async (values: RoleDetails) => {
    if (state.phase === 'form') {
      setState({
        ...state,
        phase: 'chat',
        roleDetails: {
          experienceYears: values.experienceYears,
          ...(values.focusAreas ? { focusAreas: values.focusAreas } : {}),
          jobTitle: values.jobTitle
        }
      });
      return false;
    } else if (state.phase === 'chat') {
      return await handleAnalyzeAndShowResults();
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="default"
      buttonIcon="wand-magic-sparkles"
      buttonSize="header"
      buttonText={messages.competencyMappingNew}
      contentStyle={{ padding: 0 }}
      footerStyle={{ display: 'flex', justifyContent: 'space-between' }}
      modalActionText={
        state.phase === 'form'
          ? messages.competencyMappingStartConversation
          : messages.competencyMappingAnalyzeAndShowResults
      }
      // modalActionTooltip={
      //   state.phase === 'chat' && !state.isConversationComplete
      //     ? messages.competencyMappingAssistantNotEnoughInfo
      //     : undefined
      // }
      modalTitle={messages.competencyMappingNew}
      modalWidth="wide"
      onHide={handleHide}
      onSubmit={handleSubmit}
      //submitDisabled={state.phase === 'chat' && !state.isConversationComplete}
    >
      {() =>
        state.phase === 'form' ? (
          <>
            <ProfileCompetencyMappingHeader
              description={messages.competencyMappingNewInfo}
              title={messages.competencyMappingNew}
            />

            <div className="content">
              <div className="competency-mapping-form-row competency-mapping-form-row-two-columns">
                <div className="competency-mapping-form-column">
                  <FormGroup
                    htmlFor="jobTitle"
                    label={messages.competencyMappingJobTitle}
                    maxWidth="100%"
                  >
                    <Text
                      autocomplete="off"
                      field="jobTitle"
                      id="jobTitle"
                      required={true}
                    />
                  </FormGroup>
                </div>

                <div className="competency-mapping-form-column">
                  <FormGroup
                    htmlFor="experienceYears"
                    label={messages.competencyMappingYearsOfExperience}
                    maxWidth="100%"
                  >
                    <Text
                      autocomplete="off"
                      field="experienceYears"
                      id="experienceYears"
                      min={0}
                      required={true}
                      type="number"
                    />
                  </FormGroup>
                </div>
              </div>

              <FormGroup
                htmlFor="focusAreas"
                label={messages.competencyMappingKeyResponsibilities}
                labelIsOptional={true}
                maxWidth="100%"
              >
                <TextArea
                  field="focusAreas"
                  id="focusAreas"
                  placeholder={
                    messages.competencyMappingKeyResponsibilitiesPlaceholder
                  }
                  rows={4}
                />
              </FormGroup>
            </div>

            <style jsx>{`
              .content {
                padding: 15px;
              }
              .competency-mapping-form-row {
                display: grid;
                gap: 15px;
              }
              .competency-mapping-form-row-two-columns {
                grid-template-columns: 1fr;
              }
              .competency-mapping-form-column {
                min-width: 0;
              }
              @media (min-width: ${breakpoints.small}px) {
                .competency-mapping-form-row-two-columns {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                }
              }
            `}</style>
          </>
        ) : (
          <ProfileCompetencyMappingChat setState={setState} state={state} />
        )
      }
    </ModalButtonWithForm>
  );
};

export default ProfileCompetencyMapping;
